import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone, MapPin, Heart, ShoppingBag } from "lucide-react";
import { CONTACT, ZEFFY_MAIN, SHOPIFY_URL } from "@/constants/config";
import { api } from "@/lib/api";
import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      setStatus("Mahalo! You're on the list.");
      setEmail("");
    } catch {
      setStatus("Please enter a valid email.");
    }
  };

  return (
    <footer style={{ background: "linear-gradient(180deg, #0E5C6E 0%, #0A4655 100%)" }} className="text-white pt-20 pb-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_bulldog-sanctuary/artifacts/g4vq3c1t_1782224774424.png"
                alt="Lavish 808 Bulliez Rescued Paws Foundation"
                className="w-20 h-20 object-contain"
              />
              <div className="font-serif text-2xl leading-tight">Lavish 808 Bulliez<br/><span className="text-lg text-white/80">Rescued Paws</span></div>
            </div>
            <p className="text-white/80 leading-relaxed max-w-md mb-6 font-sans">
              From Hawaii to the West Coast — rescuing, rehabilitating, and rehoming bulldogs
              through compassion, veterinary care, and the spirit of Aloha.
            </p>
            <div className="flex gap-3">
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Lavish 808 Bulliez Rescued Paws on Facebook"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="footer-facebook-link"
              >
                <Facebook size={18} aria-hidden="true" />
              </a>
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Lavish 808 Bulliez Rescued Paws on Instagram"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="footer-instagram-link"
              >
                <Instagram size={18} aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">Visit</div>
            <ul className="space-y-3 text-white/85 font-sans text-sm">
              <li className="flex gap-2"><MapPin size={16} className="mt-0.5 shrink-0" /><span>{CONTACT.address}</span></li>
              <li className="flex gap-2"><Phone size={16} className="mt-0.5 shrink-0" /><a href={`tel:${CONTACT.phone}`}>{CONTACT.phone}</a></li>
              <li className="flex gap-2"><Mail size={16} className="mt-0.5 shrink-0" /><a href={`mailto:${CONTACT.email}`} className="break-all">{CONTACT.email}</a></li>
            </ul>
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-8 mb-3">Hours</div>
            <ul className="text-white/85 font-sans text-sm space-y-1">
              {CONTACT.hours.map((h) => (
                <li key={h.day} className="flex justify-between max-w-[220px]"><span>{h.day}</span><span>{h.time}</span></li>
              ))}
            </ul>
            <a
              href={SHOPIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-sm underline text-white/85 hover:text-white transition-colors"
              data-testid="footer-shop-link"
            >
              <ShoppingBag size={14} /> Shop the merchandise
            </a>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">Stay in the ʻohana</div>
            <p className="text-white/85 text-sm mb-4">Rescue updates, adoption stories, and Aloha moments — right in your inbox.</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form" aria-label="Newsletter subscription">
              <label htmlFor="newsletter-email" className="sr-only">Email address for newsletter</label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email address"
                className="flex-1 px-4 py-3 rounded-full bg-white/10 border border-white/15 text-white placeholder-white/50 focus:outline-none focus:border-white/40 text-sm"
                data-testid="newsletter-email-input"
              />
              <button type="submit" className="btn-donate" style={{ padding: "0.75rem 1.5rem" }} data-testid="newsletter-submit">
                Subscribe
              </button>
            </form>
            {status && <div className="text-xs text-white/70 mt-2" data-testid="newsletter-status">{status}</div>}

            <a
              href={ZEFFY_MAIN}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-donate mt-6 w-full justify-center"
              data-testid="footer-donate-button"
            >
              <Heart size={16} fill="white" /> Donate via Zeffy
            </a>
          </div>
        </div>

        <div
          className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/60"
        >
          <div>
            © {new Date().getFullYear()} {CONTACT.org} • Saving dogs since 2024 • Est. 2026. 501(c)(3) nonprofit • EIN {CONTACT.ein}
          </div>
          <div className="flex gap-4">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/admin" className="hover:text-white" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>

        <div className="mt-14 text-center">
          <div className="font-serif italic text-lg text-white/50 mb-4">"{CONTACT.motto}"</div>
          <div className="font-serif text-6xl sm:text-8xl text-white/10 select-none">ʻOhana</div>
        </div>
      </div>
    </footer>
  );
}
